import { CalendarDaysIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';

import RichContent from '@/common/components/RichTextEditor/RichContent';
import { useDateLocale } from '@/common/hooks/useDateLocale';

interface NoteCardProps {
  id: string;
  title: string;
  content: string;
  createdAt: string;
  isLinkedToEvent?: boolean;
  onDelete: (id: string) => void;
  onEdit?: (id: string) => void;
}

const NoteCard = ({
  id,
  title,
  content,
  createdAt,
  isLinkedToEvent,
  onDelete,
  onEdit,
}: NoteCardProps) => {
  const { t } = useTranslation();
  const { locale } = useDateLocale();

  const timeAgo = formatDistanceToNow(new Date(createdAt), { addSuffix: true, locale });

  return (
    <div
      className="group bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200 flex flex-col h-full"
      onClick={() => onEdit?.(id)}
    >
      <div className="flex justify-between items-start p-3 pb-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">{title}</h3>
          {isLinkedToEvent && (
            <CalendarDaysIcon
              className="w-4 h-4 text-blue-500 shrink-0"
              title={t('notes.linkedToEvent')}
            />
          )}
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150 shrink-0 ml-2">
          <button
            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-blue-500 transition-colors"
            onClick={() => onEdit?.(id)}
          >
            <PencilSquareIcon className="w-4 h-4" />
          </button>
          <button
            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-red-500 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(id);
            }}
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="relative flex-1 overflow-hidden max-h-48">
        <RichContent content={content} />
        <div className="absolute bottom-0 left-0 right-0 h-8 bg-linear-to-t from-white to-transparent pointer-events-none" />
      </div>

      <div className="px-3 py-2 border-t border-gray-50">
        <span className="text-xs text-gray-400">{timeAgo}</span>
      </div>
    </div>
  );
};

export default NoteCard;
